import { Component, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  AbstractComponent,
  Channel,
  ChannelHealthClassificationEnum,
  ChannelHealthListResponse,
  ChannelHealthService,
  ChannelService,
  ChannelTypeEnum,
  isHybridChannel,
  ConfirmationComponent,
} from 'lib-trend-core';
import { translateIssueCategory, translateIssueTitle } from './channel-view/channel-health/channel-health-i18n.util';
import { ChannelConfigComponent } from './channel-config/channel-config.component';
import { ChannelEditComponent } from './channel-edit/channel-edit.component';

type ViewMode = 'cards' | 'table';
type SortMode = 'severity' | 'name';

/** Most critical first (RF-04). */
const SEVERITY_ORDER: Record<string, number> = {
  [ChannelHealthClassificationEnum.CRITICAL]: 0,
  [ChannelHealthClassificationEnum.ATTENTION]: 1,
  [ChannelHealthClassificationEnum.HEALTHY]: 2,
  [ChannelHealthClassificationEnum.NO_DATA]: 3,
};

/**
 * Channel listing with health (SPEC-2026-08-channel-health, T-19 — RF-01…RF-14).
 *
 * The health list endpoint answers every channel of the company at once (companies hold
 * few channels), so filter, search and sort are client-side. Visible to administrators
 * and supervisors (RF-36); adding/editing stays admin-only.
 */
@Component({
    selector: 'channel-component',
    templateUrl: 'channel.v2.component.html',
    styleUrl: 'channel.v2.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChannelComponent extends AbstractComponent implements OnInit {

  channels: Array<Channel> = [];
  counts: ChannelHealthListResponse['counts'] = { total: 0, critical: 0, attention: 0, healthy: 0, noData: 0 };
  lastSyncAt?: Date | string;

  viewMode: ViewMode = 'cards';
  sortMode: SortMode = 'severity';
  filterClassification: ChannelHealthClassificationEnum | null = null;
  refreshing: boolean = false;

  classification = ChannelHealthClassificationEnum;

  cloudAPI: ChannelTypeEnum = ChannelTypeEnum.CLOUD_API;
  zAPI: ChannelTypeEnum = ChannelTypeEnum.Z_API;
  instagramAPI: ChannelTypeEnum = ChannelTypeEnum.INSTAGRAM_API;
  messengerAPI: ChannelTypeEnum = ChannelTypeEnum.MESSENGER_API;
  rcsAPI: ChannelTypeEnum = ChannelTypeEnum.RCS_API;
  voipAPI: ChannelTypeEnum = ChannelTypeEnum.VOIP_API;
  webchatAPI: ChannelTypeEnum = ChannelTypeEnum.WEBCHAT_API;

  constructor(
    public injector: Injector,
    public dialog: MatDialog,
    private channelService: ChannelService,
    private channelHealthService: ChannelHealthService,
  ) {
    super(injector);
  }

  ngOnInit() {
    this.viewMode = this.restoreViewMode();
    this.getList();
  }

  getList() {
    this.loadingContent = true;
    this.channelHealthService.listHealth().subscribe({
      next: (response) => {
        this.channels = response.channels ?? [];
        this.counts = response.counts;
        this.lastSyncAt = response.lastSyncAt;
        this.loadingContent = false;
      },
      error: (err) => {
        this.loadingContent = false;
        this.alertService.error(err?.error?.message);
      },
    });
  }

  /** Company-wide on-demand refresh (RF-09) — re-reads the provider, then the list. */
  refreshAll(): void {
    if (this.refreshing) return;
    this.refreshing = true;
    this.channelHealthService.refreshCompany().subscribe({
      next: () => {
        this.refreshing = false;
        this.getList();
      },
      error: (err) => {
        this.refreshing = false;
        this.alertService.error(err?.error?.message);
      },
    });
  }

  // ---------------------------------------------------------------- presentation state

  get visibleChannels(): Array<Channel> {
    let list = [...this.channels];

    if (this.filterClassification) {
      list = list.filter(channel => this.classificationOf(channel) === this.filterClassification);
    }

    const term = (this.searchString ?? '').trim().toLowerCase();
    if (term) {
      list = list.filter(channel =>
        (channel.name ?? '').toLowerCase().includes(term)
        || (channel.identifier ?? '').toLowerCase().includes(term));
    }

    if (this.sortMode === 'name') {
      list.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
    } else {
      list.sort((a, b) =>
        (SEVERITY_ORDER[this.classificationOf(a)] - SEVERITY_ORDER[this.classificationOf(b)])
        || (a.name ?? '').localeCompare(b.name ?? ''));
    }
    return list;
  }

  search(term: string) {
    this.searchString = term;
  }

  /** The dashboard stat card renders its big figure from the `label` input (a string). */
  formatCount(value: number | null | undefined): string {
    return String(value ?? 0);
  }

  toggleFilter(classification: ChannelHealthClassificationEnum | null): void {
    this.filterClassification = this.filterClassification === classification ? null : classification;
  }

  setSort(mode: SortMode): void {
    this.sortMode = mode;
  }

  setViewMode(mode: ViewMode): void {
    this.viewMode = mode;
    try {
      localStorage.setItem(this.viewModeKey(), mode);
    } catch {
      // Storage unavailable (private mode) — the toggle still works for the session.
    }
  }

  private restoreViewMode(): ViewMode {
    try {
      const stored = localStorage.getItem(this.viewModeKey());
      return stored === 'table' ? 'table' : 'cards';
    } catch {
      return 'cards';
    }
  }

  /** Remembered per user (RF-05 / Q9). */
  private viewModeKey(): string {
    let userId = 'anon';
    try {
      userId = this.getIDCurrentUser() ?? 'anon';
    } catch {
      // No session payload (expired login screen) — fall back silently.
    }
    return `trendmulti.channel-health.viewMode.${userId}`;
  }

  // ---------------------------------------------------------------- per-channel helpers

  canManage(): boolean {
    return this.isAdmin();
  }

  canSeeList(): boolean {
    return this.isAdmin() || this.isSupervisor();
  }

  isCloud(channel: Channel): boolean {
    return channel.type === ChannelTypeEnum.CLOUD_API;
  }

  /** RF-02: Cloud API reads its stored classification; every other type is "Sem dados". */
  classificationOf(channel: Channel): ChannelHealthClassificationEnum {
    if (!this.isCloud(channel)) return ChannelHealthClassificationEnum.NO_DATA;
    return channel.health?.classification ?? ChannelHealthClassificationEnum.NO_DATA;
  }

  /** System status-badge variants (`.campaign-badge` — the shared standard). */
  badgeClass(channel: Channel): string {
    const map: Record<string, string> = {
      [ChannelHealthClassificationEnum.HEALTHY]: 'is-ok',
      [ChannelHealthClassificationEnum.ATTENTION]: 'is-warn',
      [ChannelHealthClassificationEnum.CRITICAL]: 'is-err',
      [ChannelHealthClassificationEnum.NO_DATA]: 'is-neutral',
    };
    return map[this.classificationOf(channel)];
  }

  /** The listing badges of a Hybrid channel (SPEC-2026-10-whatsapp-hybrid-channel RF-05, RF-20). */
  hybridBadges(channel: Channel): { hybrid: boolean; sessionDisconnected: boolean } {
    const hybrid = isHybridChannel(channel);
    return {
      hybrid,
      // A paired session that is down — an unpaired Hybrid channel simply runs official-only.
      sessionDisconnected: hybrid && !!channel.hybridConfig?.sessionChannel && channel.hybridConfig?.sessionConnected !== true,
    };
  }

  badgeLabel(channel: Channel): string {
    return this.t(`channel.health.badge.${this.classificationOf(channel)}`);
  }

  /** RF-07: current quality band exactly as the provider reports it. */
  qualityLabel(channel: Channel): string | null {
    if (!this.isCloud(channel)) return null;
    const band = (channel.health?.signals?.qualityRating ?? '').toUpperCase();
    const map: Record<string, string> = {
      GREEN: 'channel.health.quality.high',
      YELLOW: 'channel.health.quality.medium',
      RED: 'channel.health.quality.low',
    };
    return map[band] ? this.t(map[band]) : null;
  }

  qualityClass(channel: Channel): string {
    const band = (channel.health?.signals?.qualityRating ?? '').toUpperCase();
    if (band === 'GREEN') return 'is-ok';
    if (band === 'YELLOW') return 'is-warn';
    if (band === 'RED') return 'is-err';
    return 'is-neutral';
  }

  pendingIssueOf(channel: Channel): { title: string; category: string } | null {
    if (!this.isCloud(channel)) return null;
    const issue = channel.health?.mainPendingIssue;
    if (!issue) return null;
    const t = (key: string, params?: object) => this.t(key, params);
    return { title: translateIssueTitle(t, issue), category: translateIssueCategory(t, issue.category) };
  }

  private accessLost(channel: Channel): boolean {
    return this.classificationOf(channel) === ChannelHealthClassificationEnum.NO_DATA
      && channel.health?.mainPendingIssue?.code === 'ACCESS_LOST';
  }

  /** RF-10: why a channel shows "Sem dados". */
  noDataReason(channel: Channel): string | null {
    if (this.classificationOf(channel) !== ChannelHealthClassificationEnum.NO_DATA) return null;
    if (!this.isCloud(channel)) {
      return channel.type === ChannelTypeEnum.Z_API
        ? this.t('channel.health.noData.unofficial')
        : this.t('channel.health.noData.notCovered');
    }
    if (this.accessLost(channel)) return this.t('channel.health.noData.accessLost');
    return this.t('channel.health.noData.noReading');
  }

  /**
   * RF-09: per-channel freshness — "updated N min ago" / "last consultation" / "not applicable".
   * `short` drops the "Atualizado" prefix — the table column header already says it.
   */
  freshnessOf(channel: Channel, style: 'long' | 'short' = 'long'): string {
    if (!this.isCloud(channel)) return this.t('channel.health.freshness.notApplicable');
    const readAt = channel.health?.readAt;
    if (!readAt) return this.t('channel.health.freshness.noReading');

    if (this.accessLost(channel)) {
      return this.t('channel.health.freshness.lastConsult', { date: this.formatDateTime(readAt) });
    }

    const minutes = Math.max(0, Math.floor((Date.now() - new Date(readAt).getTime()) / 60000));
    const suffix = style === 'short' ? 'Short' : '';
    if (minutes < 60) return this.t(`channel.health.freshness.updatedMinutes${suffix}`, { minutes });
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return this.t(`channel.health.freshness.updatedHours${suffix}`, { hours });
    return this.t('channel.health.freshness.lastConsult', { date: this.formatDateTime(readAt) });
  }

  lastSyncLabel(): string | null {
    if (!this.lastSyncAt) return null;
    return this.t('channel.health.lastSync', { when: this.formatDateTime(this.lastSyncAt) });
  }

  private formatDateTime(value: Date | string): string {
    const date = new Date(value);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  }

  // ---------------------------------------------------------------- actions (unchanged behavior)

  view(channel: Channel): void {
    this.router.navigate(['/channel/view', channel._id]);
  }

  delete(channel: Channel) {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '500px',
    });
    dialogRef.afterClosed().subscribe(result => {
      if (Boolean(result) === true) {
        this.channelService.delete(channel._id).subscribe({
          next: () => {
            this.getList();
            this.alertService.success();
          },
          error: err => this.alertService.error(err.error.message)
        })
      }
    });
  }

  edit(item: Channel): void {
    const dialogRef = this.dialog.open(ChannelEditComponent, {
      width: '600px',
      data: item
    });

    dialogRef.afterClosed().subscribe(() => {
      this.getList();
    });
  }

  goStatusChannel(channel: Channel): void {
    const dialogRefStatus = this.dialog.open(ChannelConfigComponent, {
      width: '600px',
      data: channel,
    });

    dialogRefStatus.afterClosed().subscribe(() => {
      this.getList();
    });
  }

  add() {
    this.router.navigate(['/channel/add']);
  }
}
